import React, { useState } from "react";
import { Card, CardHeader, CardBody, Button } from "reactstrap";
import FeatherIcon from "feather-icons-react";

const toRgba = (hex, alpha) => {
  const value = hex.replace("#", "");
  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const ORANGE_COLORS = {
  primary: '#FF5B00',
  dark: '#e85a28',
  darker: '#d14d1f',
  light: '#ff8c61',
  lighter: '#ffb399',
  subtle: '#fff5f0',
  muted: '#ffe8dd',
};

const severityStyles = {
  Critical: {
    color: "#dc2626",
    backgroundColor: "#fef2f2",
    border: "1px solid #fecaca"
  },
  High: {
    color: "#ea580c",
    backgroundColor: "#fff7ed",
    border: "1px solid #fed7aa"
  },
  Medium: {
    color: "#d97706",
    backgroundColor: "#fffbeb",
    border: "1px solid #fde68a"
  },
  Low: {
    color: "#16a34a",
    backgroundColor: "#f0fdf4",
    border: "1px solid #bbf7d0"
  },
  "N/A": {
    color: "#6c757d", 
    backgroundColor: "rgba(108, 117, 125, 0.1)",
    border: "1px solid rgba(108, 117, 125, 0.3)" 
  },
};

const getSeverityFromLevel = (level) => {
  if (!level && level !== 0) return "N/A";
  if (level >= 11) return "Critical";
  if (level >= 8) return "High";
  if (level >= 4) return "Medium";
  return "Low";
};

const RulesTable = ({ rules = [] }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;

  const totalPages = Math.ceil(rules.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentRows = rules.slice(startIndex, startIndex + rowsPerPage);

  return (
    <Card 
      className="mt-4"
      style={{ 
        background: "var(--vz-secondary-bg, #fff)",
        border: "1px solid var(--vz-border-color)",
        borderRadius: 10,
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        overflow: "hidden"
      }}
    >
      <CardHeader className="d-flex align-items-center justify-content-between" style={{ background: "transparent", borderBottom: "1px solid var(--vz-border-color)" }}>
        <h4 className="card-title mb-0 flex-grow-1">
          <span style={{
            width: 32,
            height: 32,
            borderRadius: 8,
            border: `0.5px solid ${toRgba(ORANGE_COLORS.primary, 0.30)}`,
            background: toRgba(ORANGE_COLORS.primary, 0.10),
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            marginRight: 8,
            flexShrink: 0
          }}>
            <FeatherIcon icon="shield" size={18} style={{ color: ORANGE_COLORS.primary, lineHeight: 1 }} />
          </span>
          Rules Table
        </h4>
      </CardHeader>

      <CardBody>
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: "10%" }}>Rule ID</th>
                <th style={{ width: "45%" }}>Rule Name</th>
                <th style={{ width: "20%" }}>Severity</th>
                <th style={{ width: "25%" }}>Last Triggered</th>
              </tr>
            </thead>

            <tbody>
              {currentRows.length === 0 ? (
                <tr>
                  <td colSpan={4} className="text-center py-5">
                    <FeatherIcon icon="inbox" size={48} className="text-muted mb-3" />
                    <p className="text-muted mb-0">No rule data available.</p>
                  </td>
                </tr>
              ) : (
                currentRows.map((rule, index) => {
                  const severity = getSeverityFromLevel(rule.level);
                  const style = severityStyles[severity];

                  return (
                    <tr key={index}>
                      <td>
                        <code style={{ color: ORANGE_COLORS.dark }}>{rule.id || "N/A"}</code>
                      </td>

                      <td style={{ whiteSpace: "normal" }}>
                        <span className="fw-medium">{rule.name || "Unknown Rule"}</span>
                      </td>

                      <td>
                        <span 
                          style={style} 
                          className="badge rounded-pill"
                        >
                          {severity}
                        </span>
                      </td>

                      <td>
                        <span className="text-muted small">{rule.lastTriggered || "-"}</span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {rules.length > 0 && (
          <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
            <Button
              size="sm"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((p) => p - 1)}
              style={{
                backgroundColor: currentPage === 1 ? 'transparent' : ORANGE_COLORS.primary,
                borderColor: ORANGE_COLORS.primary,
                color: currentPage === 1 ? ORANGE_COLORS.primary : 'white'
              }}
            >
              <FeatherIcon icon="chevron-left" size={14} className="me-1" />
              Previous
            </Button>

            <span className="fw-bold">
              Page <span style={{ color: ORANGE_COLORS.primary }}>{currentPage}</span> of <span style={{ color: ORANGE_COLORS.primary }}>{totalPages}</span>
            </span>

            <Button
              size="sm"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((p) => p + 1)}
              style={{
                backgroundColor: currentPage === totalPages ? 'transparent' : ORANGE_COLORS.primary,
                borderColor: ORANGE_COLORS.primary,
                color: currentPage === totalPages ? ORANGE_COLORS.primary : 'white'
              }}
            >
              Next
              <FeatherIcon icon="chevron-right" size={14} className="ms-1" />
            </Button>
          </div>
        )}
      </CardBody>
    </Card>
  );
};

export default RulesTable;
